import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { Icon, TOP_TIP_POSITION, ToolTipWrapper } from '@src/quince-components/src'
import CheckIcon from '@src/quince-components/src/atoms/select/icons/check_circle.svg'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useEddExp from '@src/quince-site-common/src/hooks/edd/hooks/useEddExp'
import { generateEDDLabel, getCutoffTime } from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import Info from '@src/quince-site-integrated/src/assets/info_white.svg'
import { getCurrentFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

// eslint-disable-next-line import/no-named-as-default
import ZipCodeButton from '../zipCodeButton'

import styles from './eddMessageContainer.module.scss'
import OrderCutoffTime from './orderCutOffTime'

export const DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS = 'disableForceEDDForAgeRestrictedProducts'

interface EDDMessageContainerProps {
  eddDetail: EDDDetails | undefined
  product: Product
  selectedInternalVariantId: number[] | undefined
  isQuickATCForMobile?: boolean
  containerClassName?: string
  showIcon?: boolean
  showNewPdpDesignV2?: boolean
}

interface ParsedDateResult {
  before: string
  date: string
  after: string
}

export const parseDateFromLabel = (label: string): ParsedDateResult => {
  const dateRegex = /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s\d{1,2}\b/
  const match = label.match(dateRegex)
  const date = match?.[0] ?? label
  const parts = label.split(date)
  const before = parts[0] ?? ''
  const after = parts[1] ?? ''
  return { before, date, after }
}

export function EDDMessageContainer({
  eddDetail,
  product,
  selectedInternalVariantId,
  isQuickATCForMobile,
  containerClassName,
  showIcon,
  showNewPdpDesignV2,
}: EDDMessageContainerProps) {
  const { zipCode } = useZipCode()
  const { isEddExpEnabled: EDDExpEnabled } = useEddExp()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const selectedVariant = selectedInternalVariantId?.[0]?.toString()

  const disableForceEDD = useStatsigDynamicConfig<boolean>(DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS)
  const isEDDExpEnabled = EDDExpEnabled || (productHelper.getIsAgeRestrictedProduct(product) && !disableForceEDD) // force edd for age restricted products

  const { t } = useTranslation('common', { keyPrefix: 'edd' })
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    if (eddDetail?.showEDD) {
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
        // @i18n-ignore
        action: 'product_page_edd',
        // @i18n-ignore
        category: 'edd',
        context: {
          product_id: product?.spid,
          findingMethodAggregate: currentFindingMethod?.findingMethodAggregate,
          findingMethodGranular: currentFindingMethod?.findingMethodGranular,
          findingMethodId: currentFindingMethod?.findingMethodId,
          edd_standard_eligible: eddDetail.type === 'LB_STANDARD',
          edd_express_eligible: eddDetail.type === 'LB_SUPER',
          edd_max_days: eddDetail.max_days,
          edd_min_days: eddDetail.min_days,
          edd_max_date: eddDetail.max_date,
          edd_min_date: eddDetail.min_date,
          location_ids: eddDetail.inventoryLocations,
          productProperties: product && getProductPropertiesForTracking(product, selectedVariant),
          edd_label: generateEDDLabel(eddDetail, tCommon),
          is_serviceable: eddDetail.isServiceable,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eddDetail?.showEDD,
    eddDetail?.type,
    eddDetail?.max_days,
    eddDetail?.min_days,
    product?.id,
    selectedVariant,
    eddDetail?.max_date,
    eddDetail?.min_date,
    eddDetail?.inventoryLocations,
  ])

  if (!isEDDExpEnabled || eddDetail?.showEDD === undefined) return null

  if (!zipCode) {
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {t('in_stock_enter_zip_code')}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{t('zip_code')}</ZipCodeButton> {t('for_delivery_info')}
        </div>
      </div>
    )
  }

  if (!eddDetail.isServiceable) {
    return (
      <div
        data-testid="unable-to-ship"
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
          styles.errorMessageWrapper,
        )}
      >
        <div data-testid="unable-to-ship-message">{t('unable_to_ship', { zipCode: zipCode })}</div>{' '}
        <ZipCodeButton btnClassName={classnames(styles.zipcodeBtnClass, styles.zipcodeBtnClass__unableToShip)}>
          {t('update_zip_code')}
        </ZipCodeButton>
        <ToolTipWrapper
          className={styles.toolTipWrapper}
          toolTipContentClassName={styles.tooltip}
          topTipPosition={TOP_TIP_POSITION.TOP}
          toolTipContentRenderer={() => t('unable_to_ship_msg')}
        >
          <Info data-testid={`info-icon`} className={styles.infoIcon} />
        </ToolTipWrapper>
      </div>
    )
  }

  if (!eddDetail.showEDD && zipCode)
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {t('in_stock_and_ships_free_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )

  if (eddDetail.isExpressShipping) {
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {generateEDDLabel(eddDetail, tCommon)} {tCommon('with_priority_shipping_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )
  }

  if (eddDetail.type === 'LB_STANDARD_PREMIUM' && eddDetail.max_cal_days === 2) {
    const label = generateEDDLabel(eddDetail, tCommon) ?? ''
    const { before, date, after } = parseDateFromLabel(label)
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div className={styles.eddMessageWrapper__callOut}>
          <div>
            {before}
            <strong className={styles.eddMessageWrapper__bold}>{date}</strong>
            {after}
          </div>
          {eddDetail.cutoffDate && <OrderCutoffTime cutoffDate={eddDetail.cutoffDate} />}
        </div>
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {t('in_stock_and_ships_free_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )
  }

  // Calculate EDD message length for conditional styling (only on desktop)
  const eddLabel = generateEDDLabel(eddDetail, tCommon) ?? ''
  const toText = tCommon('to')
  const cutoffTimeText = eddDetail?.cutoffDate ? (getCutoffTime(eddDetail.cutoffDate, t) ?? '') : ''
  const messageText = `${eddLabel} ${toText} ${zipCode}${cutoffTimeText ? ` . ${cutoffTimeText}` : ''}`
  //max chars the flex can accomate with cutoff for edd message, anything over this should be wrapped like existing
  // Only apply cutoff style on desktop when message length <= 70
  const shouldApplyCutoffStyle = isDesktop && messageText.length <= 70

  return (
    <div className={showIcon ? styles.eddDefaultContainer : ''}>
      {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div className={shouldApplyCutoffStyle ? styles.eddMessageWithCutoff : ''}>
          {generateEDDLabel(eddDetail, tCommon)} {tCommon('to')}{' '}
          <ZipCodeButton
            btnClassName={classnames(
              styles.zipcodeBtnClass,
              shouldApplyCutoffStyle ? styles.zipcodeBtnClass__cutoff : '',
            )}
          >
            {zipCode}
          </ZipCodeButton>
          {eddDetail.cutoffDate && (
            <>
              <span className={shouldApplyCutoffStyle ? styles.desktopOnlyMsg : styles.hideDesktopMsg}>{'.'}</span>
              <OrderCutoffTime cutoffDate={eddDetail.cutoffDate} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
